import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { paymentLinksService } from '../../services/paymentLinksService';
import { paymentsService } from '../../services/paymentsService';
import { itinerariesService } from '../../services/itinerariesService';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';
import './PaymentLinksList.css';

const PaymentLinksList = () => {
  const { user } = useAuth();
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPaymentLink, setEditingPaymentLink] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSecureModal, setShowSecureModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [formData, setFormData] = useState({
    client_email: '',
    client_name: '',
    client_phone: '',
    amount: '',
    payment_type: 'custom',
    description: '',
    custom_message: '',
    agent_notes: '',
    itinerary_id: '',
    travel_end_date: '',
    expires_in_hours: 72,
    send_email: true
  });
  const [itineraries, setItineraries] = useState([]);
  const selectedFormItinerary = itineraries.find(item => String(item.id) === String(formData.itinerary_id));

  useEffect(() => {
    if (!formData.travel_end_date && selectedFormItinerary?.travel_end_date) {
      setFormData(prev => ({ ...prev, travel_end_date: String(selectedFormItinerary.travel_end_date).split('T')[0] }));
    }
  }, [formData.travel_end_date, selectedFormItinerary?.travel_end_date]);

  useEffect(() => {
    fetchPaymentLinks();
    fetchItineraries();
    // Check for pre-filled client data from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const clientParam = urlParams.get('client');
    
    if (clientParam) {
      try {
        const clientData = JSON.parse(decodeURIComponent(clientParam));
        setFormData(prev => ({
          ...prev,
          client_email: clientData.email || '',
          client_name: clientData.name || '',
          client_phone: clientData.phone || '',
          itinerary_id: clientData.itinerary_id || ''
        }));
        
        // Auto-open create modal if client data is provided
        setShowCreateModal(true);
        
        // Clean up URL parameter
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } catch (error) {
        console.error('Error parsing client data from URL:', error);
      }
    }
  }, []);

  const fetchPaymentLinks = async () => {
    try {
      setLoading(true);
      const data = await paymentLinksService.getAll();
      setPaymentLinks(data);
    } catch (error) {
      console.error('Error fetching payment links:', error);
      setError('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  const fetchItineraries = async () => {
    try {
      const data = await itinerariesService.getAll();
      // Filter to only active/upcoming itineraries
      const activeItineraries = Array.isArray(data) ? data.filter(i => i.status !== 'completed' && i.status !== 'cancelled') : [];
      setItineraries(activeItineraries);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      client_email: '',
      client_name: '',
      client_phone: '',
      amount: '',
      payment_type: 'custom',
      description: '',
      custom_message: '',
      agent_notes: '',
      itinerary_id: '',
      travel_end_date: '',
      expires_in_hours: 72,
      send_email: true
    });
  };

  const handleItineraryChange = (itineraryId) => {
    const itinerary = itineraries.find(item => String(item.id) === String(itineraryId));
    setFormData(prev => ({
      ...prev,
      itinerary_id: itineraryId,
      travel_end_date: itinerary?.travel_end_date ? String(itinerary.travel_end_date).split('T')[0] : prev.travel_end_date
    }));
  };

  const handleCreatePaymentLink = async (e) => {
    e.preventDefault();
    
    // Validate form data before sending
    if (!formData.client_name || !formData.client_email || !formData.amount) {
      alert('Please fill in all required fields: Client Name, Email, and Amount');
      return;
    }
    
    if (parseFloat(formData.amount) <= 0) {
      alert('Amount must be greater than 0');
      return;
    }
    
    try {
      setSubmitting(true);
      console.log('Creating payment link with data:', formData);
      
      // Ensure amount is a number
      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        expires_in_hours: parseInt(formData.expires_in_hours)
      };
      
      console.log('Processed payment data:', paymentData);
      
      await paymentLinksService.create(paymentData);
      await fetchPaymentLinks();
      setShowCreateModal(false);
      resetForm();
      alert('Payment link created successfully!');
    } catch (error) {
      console.error('Error creating payment link:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to create payment link: ${error.response?.data?.error || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (paymentLink) => {
    setEditingPaymentLink(paymentLink);
    setFormData({
      client_email: paymentLink.client_email,
      client_name: paymentLink.client_name,
      client_phone: paymentLink.client_phone || '',
      amount: paymentLink.amount,
      payment_type: paymentLink.payment_type,
      description: paymentLink.description || '',
      custom_message: paymentLink.custom_message || '',
      agent_notes: paymentLink.agent_notes || '',
      itinerary_id: paymentLink.itinerary_id || '',
      travel_end_date: paymentLink.travel_end_date ? String(paymentLink.travel_end_date).split('T')[0] : '',
      expires_in_hours: 72, // Default for updates
      send_email: false // Don't resend by default
    });
    setShowEditModal(true);
  };

  const handleUpdatePaymentLink = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      await paymentLinksService.update(editingPaymentLink.id, formData);
      await fetchPaymentLinks();
      setShowEditModal(false);
      setEditingPaymentLink(null);
      resetForm();
      alert('Payment link updated successfully!');
    } catch (error) {
      console.error('Error updating payment link:', error);
      alert('Failed to update payment link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPaymentLink = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this payment link?')) {
      return;
    }
    
    try {
      await paymentLinksService.cancel(id);
      await fetchPaymentLinks();
      alert('Payment link cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      alert('Failed to cancel payment link');
    }
  };

  const handleResendPaymentLink = async (id) => {
    if (!window.confirm('Are you sure you want to resend this payment link?')) {
      return;
    }
    
    try {
      await paymentLinksService.resend(id);
      alert('Payment link email resent successfully!');
    } catch (error) {
      console.error('Error resending payment link:', error);
      alert('Failed to resend payment link email');
    }
  };

  const handleSendReminder = async (id) => {
    try {
      await paymentLinksService.sendReminder(id);
      alert('Payment reminder sent successfully!');
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      alert(`Failed to send reminder: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleViewPaymentDetails = async (paymentLinkId) => {
    try {
      // Find the payment link to get the payment_id
      const paymentLink = paymentLinks.find(link => link.id === paymentLinkId);
      if (!paymentLink || !paymentLink.payment_id) {
        alert('Payment details not available for this link');
        return;
      }

      // Create a mock quote object for the Payments modal (it expects quote with payment_ids)
      const mockQuote = {
        payment_ids: [paymentLink.payment_id],
        payment_count: 1,
        quote_title: paymentLink.description || 'Payment Link Transaction',
        client_name: paymentLink.client_name,
        amount: paymentLink.amount,
        paymentLinkId: paymentLinkId, // Add the payment link ID for deletion
        paymentLinkStatus: paymentLink.status // Add status to determine if deletion is allowed
      };
      
      setSelectedPayment(mockQuote);
      setShowSecureModal(true);
    } catch (error) {
      console.error('Error preparing payment details:', error);
      alert('Failed to load payment details');
    }
  };

  const closeSecureModal = () => {
    setShowSecureModal(false);
    setSelectedPayment(null);
  };

  const copyPaymentLink = (token) => {
    const paymentUrl = `${window.location.origin}/payment-link/${token}`;
    navigator.clipboard.writeText(paymentUrl).then(() => {
      alert('Payment link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  // Bulk operations
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLinks(filteredPaymentLinks.map(link => link.id));
    } else {
      setSelectedLinks([]);
    }
  };

  const handleSelectLink = (linkId, checked) => {
    if (checked) {
      setSelectedLinks([...selectedLinks, linkId]);
    } else {
      setSelectedLinks(selectedLinks.filter(id => id !== linkId));
    }
  };

  const handleBulkCancel = async () => {
    if (!window.confirm(`Are you sure you want to cancel ${selectedLinks.length} payment links?`)) {
      return;
    }
    
    try {
      await Promise.all(selectedLinks.map(id => paymentLinksService.cancel(id)));
      await fetchPaymentLinks();
      setSelectedLinks([]);
      alert(`${selectedLinks.length} payment links cancelled successfully!`);
    } catch (error) {
      console.error('Error cancelling payment links:', error);
      alert('Failed to cancel some payment links');
    }
  };

  const handleBulkResend = async () => {
    if (!window.confirm(`Are you sure you want to resend ${selectedLinks.length} payment links?`)) {
      return;
    }
    
    try {
      await Promise.all(selectedLinks.map(id => paymentLinksService.resend(id)));
      setSelectedLinks([]);
      alert(`${selectedLinks.length} payment links resent successfully!`);
    } catch (error) {
      console.error('Error resending payment links:', error);
      alert('Failed to resend some payment links');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Enhanced filtering and sorting logic
  const filteredPaymentLinks = paymentLinks
    .filter(link => {
      // Search filter
      const matchesSearch = 
        link.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.amount?.toString().includes(searchTerm);
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || link.status === statusFilter;
      
      // Payment type filter
      const matchesPaymentType = paymentTypeFilter === 'all' || link.payment_type === paymentTypeFilter;
      
      // Date filter
      let matchesDate = true;
      if (dateFilter !== 'all') {
        const linkDate = new Date(link.created_at);
        const now = new Date();
        
        switch (dateFilter) {
          case 'today':
            matchesDate = linkDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = linkDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = linkDate >= monthAgo;
            break;
          case 'expired':
            matchesDate = link.status === 'pending' && new Date() > new Date(link.expires_at);
            break;
        }
      }
      
      return matchesSearch && matchesStatus && matchesPaymentType && matchesDate;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'client_name':
          aValue = a.client_name?.toLowerCase() || '';
          bValue = b.client_name?.toLowerCase() || '';
          break;
        case 'amount':
          aValue = parseFloat(a.amount) || 0;
          bValue = parseFloat(b.amount) || 0;
          break;
        case 'expires_at':
          aValue = new Date(a.expires_at);
          bValue = new Date(b.expires_at);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default: // created_at
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'badge badge-warning',
      completed: 'badge badge-success',
      expired: 'badge badge-secondary',
      cancelled: 'badge badge-danger'
    };
    return statusClasses[status] || 'badge badge-gray';
  };

  const getPaymentTypeBadge = (type) => {
    const typeClasses = {
      deposit: 'badge badge-blue',
      final_payment: 'badge badge-purple',
      installment: 'badge badge-orange',
      custom: 'badge badge-green'
    };
    return typeClasses[type] || 'badge badge-gray';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    return new Date() > new Date(expiresAt);
  };

  if (loading) {
    return <div className="loading">Loading payment links...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="payment-links-list">
      <div className="page-header">
        <h1>Payment Links</h1>
        <p className="page-description">
          Create and manage payment collection links for your clients. 
          Send secure payment links via email for flexible payment collection.
        </p>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => window.location.href = '/agent/payment-reports'}
            title="View submitted payment information and analytics"
          >
            📊 View Payment Reports
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Payment Link
          </button>
        </div>
      </div>

      {/* Enhanced Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by client name, email, description, or amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select
            value={paymentTypeFilter}
            onChange={(e) => setPaymentTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="custom">Custom</option>
            <option value="deposit">Deposit</option>
            <option value="final_payment">Final Payment</option>
            <option value="installment">Installment</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Date:</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Sort:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="created_at">Date Created</option>
            <option value="client_name">Client Name</option>
            <option value="amount">Amount</option>
            <option value="expires_at">Expiration</option>
            <option value="status">Status</option>
          </select>
          <button
            className="sort-order-btn"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedLinks.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-selection-info">
            <span>{selectedLinks.length} payment link{selectedLinks.length !== 1 ? 's' : ''} selected</span>
            <button 
              className="btn btn-sm btn-secondary"
              onClick={() => setSelectedLinks([])}
            >
              Clear Selection
            </button>
          </div>
          <div className="bulk-actions">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleBulkResend}
              disabled={!selectedLinks.some(id => {
                const link = paymentLinks.find(l => l.id === id);
                return link && link.status === 'pending' && !isExpired(link.expires_at);
              })}
            >
              📧 Resend Selected
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleBulkCancel}
              disabled={!selectedLinks.some(id => {
                const link = paymentLinks.find(l => l.id === id);
                return link && link.status === 'pending';
              })}
            >
              ❌ Cancel Selected
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Links</h3>
          <div className="summary-value">{filteredPaymentLinks.length}</div>
        </div>
        <div className="summary-card">
          <h3>Pending</h3>
          <div className="summary-value">
            {filteredPaymentLinks.filter(l => l.status === 'pending').length}
          </div>
        </div>
        <div className="summary-card">
          <h3>Completed</h3>
          <div className="summary-value">
            {filteredPaymentLinks.filter(l => l.status === 'completed').length}
          </div>
        </div>
        <div className="summary-card">
          <h3>Total Amount</h3>
          <div className="summary-value">
            ${filteredPaymentLinks
              .filter(l => l.status === 'completed')
              .reduce((sum, l) => sum + parseFloat(l.amount || 0), 0)
              .toFixed(2)}
          </div>
        </div>
      </div>

      {/* Payment Links Table */}
      {filteredPaymentLinks.length === 0 ? (
        <div className="no-data">
          <p>No payment links found matching your criteria.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="payment-links-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={selectedLinks.length === filteredPaymentLinks.length && filteredPaymentLinks.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="sortable" onClick={() => handleSort('created_at')}>
                  Created {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable" onClick={() => handleSort('client_name')}>
                  Client {sortBy === 'client_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable" onClick={() => handleSort('amount')}>
                  Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Type</th>
                <th className="sortable" onClick={() => handleSort('status')}>
                  Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable" onClick={() => handleSort('expires_at')}>
                  Expires {sortBy === 'expires_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Travel End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPaymentLinks.map((link) => (
                <tr key={link.id} className={isExpired(link.expires_at) && link.status === 'pending' ? 'expired-row' : ''}>
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectedLinks.includes(link.id)}
                      onChange={(e) => handleSelectLink(link.id, e.target.checked)}
                    />
                  </td>
                  <td>{formatDate(link.created_at)}</td>
                  <td>
                    <div className="client-info">
                      <strong>{link.client_name}</strong>
                      <small>{link.client_email}</small>
                    </div>
                  </td>
                  <td>
                    <span className="amount">${parseFloat(link.amount || 0).toFixed(2)}</span>
                  </td>
                  <td>
                    <span className={getPaymentTypeBadge(link.payment_type)}>
                      {link.payment_type?.replace('_', ' ') || 'Custom'}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadge(link.status)}>
                      {link.status}
                    </span>
                  </td>
                  <td>
                    <div className={isExpired(link.expires_at) ? 'expired' : ''}>
                      {formatDate(link.expires_at)}
                    </div>
                  </td>
                  <td>{link.travel_end_date ? formatDate(link.travel_end_date) : 'Not provided'}</td>
                  <td>
                    <div className="actions">
                      {link.status === 'pending' && !isExpired(link.expires_at) && (
                        <>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => copyPaymentLink(link.session_token)}
                            title="Copy payment link"
                          >
                            📋 Copy Link
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleResendPaymentLink(link.id)}
                            title="Resend email"
                          >
                            📧 Resend
                          </button>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleSendReminder(link.id)}
                            title="Send reminder email"
                          >
                            🔔 Remind
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditClick(link)}
                            title="Edit payment link"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleCancelPaymentLink(link.id)}
                            title="Cancel payment link"
                          >
                            ❌ Cancel
                          </button>
                        </>
                      )}
                      {link.status === 'completed' && (
                        <div className="completed-info">
                          <span>
                            Completed on {formatDate(link.completed_at)}
                            {link.confirmation_number && (
                              <small>Confirmation: {link.confirmation_number}</small>
                            )}
                          </span>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditClick(link)}
                            title="Edit agent notes"
                          >
                            📝 Edit Notes
                          </button>
                          <button
                            className="btn btn-info payment-details-btn"
                            onClick={() => handleViewPaymentDetails(link.id)}
                            title="View secure payment details"
                          >
                            💳 View Payment Details
                          </button>
                        </div>
                      )}
                      {(link.status === 'expired' || (link.status === 'pending' && isExpired(link.expires_at))) && (
                        <span className="expired-info">
                          Expired
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Payment Link</h2>
              <button className="close-button" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreatePaymentLink}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Client Email *</label>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Client Phone</label>
                  <input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Type</label>
                    <select
                      value={formData.payment_type}
                      onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                      className="form-input"
                    >
                      <option value="custom">Custom</option>
                      <option value="deposit">Deposit</option>
                      <option value="final_payment">Final Payment</option>
                      <option value="installment">Installment</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Trip deposit for Hawaii vacation"
                  />
                </div>

                <div className="form-group">
                  <label>Custom Message</label>
                  <textarea
                    value={formData.custom_message}
                    onChange={(e) => setFormData({...formData, custom_message: e.target.value})}
                    className="form-input"
                    rows="3"
                    placeholder="Add a personalized message for the client..."
                  />
                </div>

                <div className="form-group">
                  <label>Agent Notes (Internal Only)</label>
                  <textarea
                    value={formData.agent_notes}
                    onChange={(e) => setFormData({...formData, agent_notes: e.target.value})}
                    className="form-input"
                    rows="2"
                    placeholder="Internal notes (not visible to client) - e.g., loyalty numbers, DOB, special requests..."
                  />
                  <small className="text-gray-500">These notes are only visible to you and other agents</small>
                </div>

                <div className="form-group">
                  <label>Link to Itinerary (Optional)</label>
                  <select
                    value={formData.itinerary_id}
                    onChange={(e) => handleItineraryChange(e.target.value)}
                    className="form-input"
                  >
                    <option value="">None - Standalone Payment Link</option>
                    {itineraries.map(itinerary => (
                      <option key={itinerary.id} value={itinerary.id}>
                        {itinerary.title} - {itinerary.client_name || 'No Client'} (${parseFloat(itinerary.total_amount || 0).toFixed(2)})
                      </option>
                    ))}
                  </select>
                  <small className="text-gray-500">When linked, payment will auto-record as client payment on the itinerary</small>
                </div>

                <div className="form-group">
                  <label>Travel End Date (Internal Only, Optional)</label>
                  <input
                    type="date"
                    value={formData.travel_end_date}
                    onChange={(e) => setFormData({...formData, travel_end_date: e.target.value})}
                    className="form-input"
                    disabled={Boolean(selectedFormItinerary?.travel_end_date)}
                  />
                  <small className="text-gray-500">This date is only visible to agents</small>
                </div>

                <div className="form-group">
                  <label>Link Expires In</label>
                  <select
                    value={formData.expires_in_hours}
                    onChange={(e) => setFormData({...formData, expires_in_hours: parseInt(e.target.value)})}
                    className="form-input"
                  >
                    <option value={24}>24 Hours</option>
                    <option value={72}>3 Days</option>
                    <option value={168}>7 Days</option>
                    <option value={336}>14 Days</option>
                    <option value={720}>30 Days</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.send_email}
                      onChange={(e) => setFormData({...formData, send_email: e.target.checked})}
                    />
                    Payment Link will be emailed to Client upon creation
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Payment Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPaymentLink?.status === 'completed' ? 'Edit Internal Details' : 'Edit Payment Link'}</h2>
              <button className="close-button" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdatePaymentLink}>
              <div className="modal-body">
                {editingPaymentLink?.status === 'completed' && (
                  <div className="alert alert-info" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px' }}>
                    <strong>Note:</strong> This payment has been completed. {editingPaymentLink?.travel_end_date_editable ? 'Agent notes and the travel end date can be updated.' : 'Agent notes can be updated. The travel end date can no longer be changed here.'}
                  </div>
                )}

                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="form-input"
                    required
                    disabled={editingPaymentLink?.status === 'completed'}
                  />
                </div>

                <div className="form-group">
                  <label>Client Email *</label>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    className="form-input"
                    required
                    disabled={editingPaymentLink?.status === 'completed'}
                  />
                </div>

                <div className="form-group">
                  <label>Client Phone</label>
                  <input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                    className="form-input"
                    disabled={editingPaymentLink?.status === 'completed'}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      className="form-input"
                      required
                      disabled={editingPaymentLink?.status === 'completed'}
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Type</label>
                    <select
                      value={formData.payment_type}
                      onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                      className="form-input"
                      disabled={editingPaymentLink?.status === 'completed'}
                    >
                      <option value="custom">Custom</option>
                      <option value="deposit">Deposit</option>
                      <option value="final_payment">Final Payment</option>
                      <option value="installment">Installment</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Trip deposit for Hawaii vacation"
                    disabled={editingPaymentLink?.status === 'completed'}
                  />
                </div>

                <div className="form-group">
                  <label>Custom Message</label>
                  <textarea
                    value={formData.custom_message}
                    onChange={(e) => setFormData({...formData, custom_message: e.target.value})}
                    className="form-input"
                    rows="3"
                    placeholder="Add a personalized message for the client..."
                    disabled={editingPaymentLink?.status === 'completed'}
                  />
                </div>

                <div className="form-group">
                  <label>Agent Notes (Internal Only)</label>
                  <textarea
                    value={formData.agent_notes}
                    onChange={(e) => setFormData({...formData, agent_notes: e.target.value})}
                    className="form-input"
                    rows="2"
                    placeholder="Internal notes (not visible to client) - e.g., loyalty numbers, DOB, special requests..."
                  />
                  <small className="text-gray-500">These notes are only visible to you and other agents</small>
                </div>

                <div className="form-group">
                  <label>Travel End Date (Internal Only, Optional)</label>
                  <input
                    type="date"
                    value={formData.travel_end_date}
                    onChange={(e) => setFormData({...formData, travel_end_date: e.target.value})}
                    className="form-input"
                    disabled={editingPaymentLink?.travel_end_date_editable === false || Boolean(selectedFormItinerary?.travel_end_date)}
                  />
                  <small className="text-gray-500">This date is only visible to agents</small>
                </div>

                {editingPaymentLink?.status !== 'completed' && (
                  <>
                    <div className="form-group">
                      <label>Link to Itinerary (Optional)</label>
                      <select
                        value={formData.itinerary_id}
                        onChange={(e) => handleItineraryChange(e.target.value)}
                        className="form-input"
                      >
                        <option value="">None - Standalone Payment Link</option>
                        {itineraries.map(itinerary => (
                          <option key={itinerary.id} value={itinerary.id}>
                            {itinerary.title} - {itinerary.client_name || 'No Client'} (${parseFloat(itinerary.total_amount || 0).toFixed(2)})
                          </option>
                        ))}
                      </select>
                      <small className="text-gray-500">When linked, payment will auto-record as client payment on the itinerary</small>
                    </div>

                    <div className="form-group">
                      <label>Extend Expiration</label>
                      <select
                        value={formData.expires_in_hours}
                        onChange={(e) => setFormData({...formData, expires_in_hours: parseInt(e.target.value)})}
                        className="form-input"
                      >
                        <option value={24}>+24 Hours</option>
                        <option value={72}>+3 Days</option>
                        <option value={168}>+7 Days</option>
                        <option value={336}>+14 Days</option>
                        <option value={720}>+30 Days</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Updating...' : (editingPaymentLink?.status === 'completed' ? 'Save Changes' : 'Update Payment Link')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Secure Payment Details Modal */}
      {showSecureModal && selectedPayment && (
        <SecurePaymentDetailsModal
          isOpen={showSecureModal}
          quote={selectedPayment}
          onClose={closeSecureModal}
          onPaymentDeleted={fetchPaymentLinks}
          isPaymentLink={true}
          paymentLinkId={selectedPayment.paymentLinkId}
        />
      )}
    </div>
  );
};

export default PaymentLinksList;
